import { useState } from 'react'
import { Mail, Phone, CheckCircle, AlertCircle } from 'lucide-react'
import emailjs from '@emailjs/browser'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import map1Image from '@/assets/images/maps/map1.png'
import map2Image from '@/assets/images/maps/map2.png'

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')
    setErrorMessage('')

    try {
      // EmailJS configuration
      const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID || ''
      const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID || ''
      const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY || ''

      if (!serviceId || !templateId || !publicKey) {
        throw new Error('EmailJS configuration is missing. Please check your environment variables.')
      }

      // Send email via EmailJS
      await emailjs.send(
        serviceId,
        templateId,
        {
          from_name: formData.name,
          from_email: formData.email,
          message: formData.message,
          to_email: 'support@taktcon.com',
        },
        publicKey
      )
      
      // Show success message
      setSubmitStatus('success')
      setFormData({ name: '', email: '', message: '' })
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus('idle')
      }, 5000)
    } catch (error) {
      console.error('EmailJS error:', error)
      setSubmitStatus('error')
      setErrorMessage(
        error instanceof Error 
          ? error.message 
          : 'Failed to send message. Please try again or email us directly at support@taktcon.com'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  return (
    <>
      <Header currentPage="contact" />

      {/* Contact Header */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            CONTACT US
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            We'd love to hear from you. Reach out to us for any inquiries.
          </p>
        </div>
      </section>

      {/* Maps and Office Information */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            
            {/* Head Office */}
            <div className="space-y-6">
              <div className="bg-gray-100 rounded-lg overflow-hidden">
                <img 
                  src={map1Image} 
                  alt="Head Office Map" 
                  className="w-full h-64 object-cover"
                />
              </div>
              
              <div className="space-y-4 text-center">
                <h2 className="text-2xl font-bold text-gray-900 text-center">
                  HEAD OFFICE
                </h2>
                <div className="space-y-2 text-gray-600">
                  <p className="font-medium">Administration & Accounting</p>
                  <p>TAKT Construction Services Ltd.</p>
                  <p>4388 Still Creek Drive</p>
                  <p>Unit 219</p>
                  <p>Burnaby, BC. V5C 6C6</p>
                  <div className="flex items-center justify-center space-x-2 pt-2">
                    <Mail className="h-4 w-4 text-blue-600" />
                    <a href="mailto:support@taktcon.com" className="text-blue-600 hover:text-blue-800">
                      support@taktcon.com
                    </a>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <Phone className="h-4 w-4 text-blue-600" />
                    <a href="tel:+16048428258" className="text-blue-600 hover:text-blue-800">
                      +1-604-842-8258
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* GVR Pick Up & Deliveries */}
            <div className="space-y-6">
              <div className="bg-gray-100 rounded-lg overflow-hidden">
                <img 
                  src={map2Image} 
                  alt="GVR Pick Up & Deliveries Map" 
                  className="w-full h-64 object-cover"
                />
              </div>
              
              <div className="space-y-4 text-center">
                <h2 className="text-2xl font-bold text-gray-900 text-center">
                GVR PICK UP & DELIVERIES
              </h2>
                <div className="space-y-2 text-gray-600">
                  <p className="font-medium">Greater Vancouver Region</p>
                  <p>Product Pick Up & Deliveries Only</p>
                  <p>TAKT Construction Services Ltd.</p>
                  <p>3680 East 4th Avenue</p>
                  <p>Unit 2135 [Warehouse]</p>
                  <p>Vancouver, BC. V5M 1M2</p>
                  <p className="font-semibold text-blue-600 pt-2">
                    ** Seven days a week 9am-8pm **
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Hours */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm border p-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                  Business Hours
                </h3>
            <div className="space-y-2 text-gray-600">
              <div className="flex justify-between">
                <span>Monday - Friday:</span>
                <span className="font-medium">8:00AM - 5:00PM</span>
              </div>
              <div className="flex justify-between">
                <span>Saturday:</span>
                <span className="font-medium text-red-600">Closed</span>
              </div>
              <div className="flex justify-between">
                <span>Sunday:</span>
                <span className="font-medium text-red-600">Closed</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Send us a Message
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Success Message */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4 flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Message sent successfully!</p>
                    <p className="text-sm">Thank you for contacting us. We'll get back to you soon!</p>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 flex items-center space-x-3">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Error sending message</p>
                    <p className="text-sm">{errorMessage}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your Email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Your Message"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
                  required
                  disabled={isSubmitting}
                ></textarea>
              </div>
              
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200 transform hover:scale-105 disabled:transform-none"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </>
  )
} 